/**
 * KANBAN BOARD PAGE — rotta `/kanban/:id`. Bacheca **di qualunque tipo**, non del preventivo.
 *
 * La pagina non conosce nessuna entità: dalla `className` della bacheca prende il descrittore del
 * tipo e usa la sua sezione `board` per **caricare**, **leggere lo stato**, **spostare** e
 * **disegnare** la card. Un tipo nuovo entra in bacheca dichiarando quella sezione, senza che questo
 * file cambi — e se non la dichiara, la guardia lo dice invece di aprire colonne vuote.
 *
 * Le colonne vengono dalla pipeline della bacheca. Gli elementi si caricano dal REST **del tipo** con
 * `?kanbanId=`, non da un endpoint neutro: è il server a risolvere il filtro, e il DTO che torna è
 * quello pieno, così la card mantiene i propri campi invece di un minimo comune denominatore.
 *
 * **Perché lo spostamento ottimistico non muta l'elemento.** Gli elementi qui sono opachi
 * (`Record<string, unknown>`): la pagina sa *leggerne* lo stato con `stateIdOf`, non scriverlo. Lo
 * spostamento in volo vive quindi in una mappa a parte, sovrapposta alla lettura. Il rollback è
 * togliere una chiave, e togliere una chiave non può sovrascrivere lo spostamento di un'altra card
 * ancora in volo — che è la proprietà che l'updater funzionale garantiva prima.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useIsClassNameDisabled } from '../../plugin/useCoreItems';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { useToast } from '../../components/useToast';
import { KanbanBoard, type KanbanColumn, type KanbanBoardItem } from '../../components/KanbanBoard';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { EditableField, type EditableValue } from '../../components/EditableField';
import { KanbanFilterEditor } from './KanbanFilterEditor';
import { kanbanApi } from './kanbanApi';
import { getEntityShortNameByFqn, getEntityType, getEntityLabelByFqn } from '../../entityRegistry/entityRegistry';
import { fetchKanban, updateKanban } from './kanbanThunks';
import { clearCurrentKanban } from './kanbanSlice';
import { fetchPipeline } from '../pipeline/pipelineThunks';
import { clearCurrentPipeline } from '../pipeline/pipelineSlice';
import { pipelineStateLabel, sortedStates } from '../pipeline/pipelineUtils';
import type { KanbanSliceState } from './kanbanTypes';
import type { KanbanFilter, KanbanFilterField } from './kanbanMappers';
import type { PipelineSliceState } from '../pipeline/pipelineTypes';

export default function KanbanBoardPage() {
  const { id } = useParams<{ id: string }>();
  const kanbanId = Number(id);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();
  const groupId = useCurrentGroupId();

  const kanban = useAppSelector((s) => (s.kanbans as KanbanSliceState).currentItem);
  const pipeline = useAppSelector((s) => (s.pipelines as PipelineSliceState).currentItem);

  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  // Spostamenti in volo o gia' confermati, per id: si sovrappongono a `stateIdOf`.
  const [movedStates, setMovedStates] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  // Bozza: si scrive qui e si applica al salvataggio, cosi' "Annulla" torna davvero
  // indietro. Toccando il filtro della board non ci sarebbe niente a cui tornare.
  const [draft, setDraft] = useState<KanbanFilter | null>(null);
  const [savingFilter, setSavingFilter] = useState(false);
  const [filterFields, setFilterFields] = useState<KanbanFilterField[]>([]);
  // Entra fra le dipendenze del fetch: lo rilancia anche quando groupId e kanbanId
  // non cambiano, cioe' dopo un salvataggio del filtro.
  const [reload, setReload] = useState(0);

  const ready = kanban?.kanbanId === kanbanId;
  const isClassNameDisabled = useIsClassNameDisabled();
  const pipelineId = ready ? kanban.pipelineId : 0;

  // TS-117: questa board si sa disegnare?
  //
  // Un tipo puo' avere un contributore lato server e nessuna sezione `board` nel proprio
  // descrittore: plugin spento, oppure backend arrivato prima della UI. In quel caso la pagina lo
  // DICE, invece di caricare preventivi per una board che preventivi non ne contiene e mostrare
  // colonne vuote — che si leggerebbero come "non ho record", cioe' la risposta giusta a un'altra
  // domanda.
  const typeName = ready ? getEntityShortNameByFqn(kanban.className) : undefined;
  const board = typeName ? getEntityType(typeName)?.board : undefined;
  const canRender = Boolean(board);

  // 1) La bacheca (nome, pipelineId).
  useEffect(() => {
    dispatch(fetchKanban(kanbanId));
    return () => {
      dispatch(clearCurrentKanban());
    };
  }, [dispatch, kanbanId]);

  // 2) La pipeline (colonne).
  useEffect(() => {
    if (!pipelineId) return;
    dispatch(fetchPipeline(pipelineId));
    return () => {
      dispatch(clearCurrentPipeline());
    };
  }, [dispatch, pipelineId]);

  // 3) Gli elementi della board: dal REST DEL TIPO, filtro risolto lato server via kanbanId.
  useEffect(() => {
    if (!ready || !groupId || !board) return;
    let alive = true;
    setLoading(true);
    board
      .loadItems(kanbanId, groupId)
      .then((page) => {
        if (!alive) return;

        setItems(page.items);
        // Il carico rilegge lo stato vero: gli spostamenti sovrapposti hanno finito il loro compito.
        setMovedStates({});
      })
      .catch((err) => {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [ready, groupId, kanbanId, reload, toast, board]);

  // Il catalogo dei campi filtrabili e' DEL TIPO della bacheca, quindi si aspetta che la bacheca
  // sia arrivata: chiederlo prima vorrebbe dire chiederlo per nessuno.
  useEffect(() => {
    if (!groupId || !ready) return;
    let alive = true;
    kanbanApi.getFilterFields(groupId, kanban.className)
      .then((f) => { if (alive) setFilterFields(f); })
      // Il catalogo che NON arriva non e' un catalogo vuoto. Il catch qui taceva, e un 500
      // sull'endpoint diventava in maschera "questo tipo non ha campi filtrabili": indistinguibile
      // dalla risposta legittima, col comando del filtro disabilitato e nessuna spiegazione. E' il
      // difetto che ci e' costato la mattina del 2026-09-17 su una chiave i18n mancante.
      .catch((err) => {
        if (!alive) return;
        setFilterFields([]);
        toast({
          severity: 'error',
          message: t('kanban.filter.catalogFailed', {
            reason: err instanceof Error ? err.message : String(err),
          }),
        });
      });
    return () => { alive = false; };
  }, [groupId, ready, kanban?.className, toast, t]);

  const states = sortedStates(pipeline?.pipelineId === pipelineId ? pipeline : undefined);
  const initialStateId =
    states.find((s) => s.kind === 'initial')?.pipelineStateId ?? states[0]?.pipelineStateId ?? 0;
  const columnIds = new Set(states.map((s) => s.pipelineStateId));

  // Tono/colore colonna dalla caratteristica dello stato: iniziale=primary, vinto=green,
  // perso=red, normale=default (vedi .kanban-column[data-tone] in _components.scss).
  const columns: KanbanColumn[] = states.map((s) => ({
    id: s.pipelineStateId,
    title: pipelineStateLabel(s),
    tone: s.kind === 'initial' ? 'initial' : s.kind === 'won' ? 'won' : s.kind === 'lost' ? 'lost' : 'default',
  }));

  const boardItems: KanbanBoardItem<Record<string, unknown>>[] = items.map((item) => {
    // L'id lo dice il descrittore. Ricavarlo qui dal nome del tipo sembrava funzionare e non
    // funzionava: `typeName` e' 'Quotation' con la maiuscola e il campo e' `quotationId`, quindi
    // ogni card prendeva id 0 e il trascinamento mostrava l'anteprima sbagliata.
    const id = board ? board.idOf(item) : 0;
    // Lo spostamento in volo vince sulla lettura: e' quello che l'utente ha appena fatto.
    const stateId = movedStates[id] ?? (board ? board.stateIdOf(item) : 0);

    return {
      id,
      // Uno stato che non e' una colonna di QUESTA board (record non collocato, o collocato in un
      // altro funnel) si disegna nella colonna iniziale: si vede, invece di sparire.
      columnId: columnIds.has(stateId) ? stateId : initialStateId,
      data: item,
    };
  });

  // Rinomina inline del titolo (stesso pattern dei campi dei detail: EditableField →
  // updateKanban). `update.fulfilled` aggiorna il currentItem, quindi il titolo resta
  // coerente col nuovo nome senza refetch.
  const commitName = useCallback(
    async (newValue: EditableValue) => {
      try {
        await dispatch(updateKanban({ kanbanId, name: String(newValue).trim() })).unwrap();
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    },
    [dispatch, kanbanId, toast, t],
  );

  // Quante condizioni ha il filtro: dice a colpo d'occhio che la board e' ristretta e di
  // quanto. Una colonna semivuota non e' un dato mancante, e' un filtro.
  const conditionCount = useMemo(
    () => (kanban?.filter?.groups ?? []).reduce((n, g) => n + g.conditions.length, 0),
    [kanban?.filter],
  );

  // Condizioni ORFANE: puntano a campi che il catalogo non dichiara piu'. Il server le
  // SCARTA invece di rifiutare il filtro, cosi' la board resta apribile e il filtro
  // correggibile — ma mostra piu' di quanto il filtro dica, e va detto.
  // Il confronto lo fa il client, che il catalogo ce l'ha gia': nessuna chiamata in piu'.
  // Si aspetta il catalogo, o senza sembrerebbero tutte orfane.
  const orphans = useMemo(
    () => (!filterFields.length ? [] : (kanban?.filter?.groups ?? [])
      .flatMap((g) => g.conditions)
      .filter((c) => !filterFields.some(
        (f) => f.scope === c.scope && f.fieldName === c.fieldName))),
    [kanban?.filter, filterFields],
  );

  const saveFilter = useCallback(async () => {
    setSavingFilter(true);
    try {
      await dispatch(updateKanban({ kanbanId, filter: draft })).unwrap();
      setFilterOpen(false);

      // Salvare il filtro POPOLA la bacheca: entrano i record che lo soddisfano e che non
      // appartengono ad alcun funnel. E' una chiamata a parte e non un effetto della PATCH perche'
      // e' ripetibile, e perche' il suo esito e' un NUMERO che va detto.
      //
      // Il popolamento sta in un try suo: se fallisce, il filtro e' comunque salvato, e dirlo con
      // l'errore del salvataggio farebbe credere che sia da riscrivere.
      let recruited: number | null = null;

      try {
        recruited = await kanbanApi.populate(kanbanId);
      } catch (err) {
        toast({
          severity: 'warning',
          message: t('kanban.populate.failed', {
            reason: err instanceof Error ? err.message : String(err),
          }),
        });
      }

      // Rilettura dal server: un filtro allargato fa ENTRARE record che in pagina non ci sono —
      // sia perche' il filtro li accetta, sia perche' il popolamento li ha appena collocati — e
      // rifiltrare quelli presenti li perderebbe.
      setReload((n) => n + 1);

      // Con il conteggio si dice quello; senza, resta il "salvato" di sempre.
      toast(
        recruited === null
          ? { severity: 'success', message: t('common.saved') }
          : { severity: 'success', message: t('kanban.populate.done', { count: recruited }) },
      );
    } catch (err) {
      // Il dialog resta aperto: il filtro rifiutato deve restare sotto le mani di chi lo
      // ha scritto, che e' l'unico posto da cui correggerlo.
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSavingFilter(false);
    }
  }, [dispatch, kanbanId, draft, toast, t]);

  const handleMove = useCallback(
    (itemId: string | number, targetColumnId: string | number) => {
      if (!board) return;

      // Gli id della board sono generici (string|number); nel CRM sono sempre numerici.
      const id = Number(itemId);
      const targetStateId = Number(targetColumnId);

      // Ottimismo: la card si sposta subito, e lo spostamento vive nella mappa invece che
      // nell'elemento, che la pagina non sa modificare.
      setMovedStates((cur) => ({ ...cur, [id]: targetStateId }));

      board.move(id, targetStateId).catch((err) => {
        // Rollback SOLO di questa card: si toglie la sua chiave, e togliere una chiave non puo'
        // sovrascrivere lo spostamento di un'altra card ancora in volo.
        setMovedStates((cur) => {
          const next = { ...cur };

          delete next[id];

          return next;
        });

        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      });
    },
    [board, toast],
  );

  // Board di un tipo spento dalla pagina «Voci del CRM» (FILO-53): come le rotte della voce, alla
  // home. Dopo tutti gli hook, che non possono stare dietro a un return.
  if (ready && isClassNameDisabled(kanban.className)) return <Navigate to="/" replace />;

  return (
    // Niente padding proprio: la pagina appoggia su quello di `.main`, come le
    // altre. Con `p-xl` il contenuto partiva piu' a destra di ogni altra pagina.
    <div>
      {/* Stesso breadcrumb del dettaglio contatto: "torna" in grigio come primo
          segmento, nome della bacheca in evidenza come ultimo. */}
      <nav className="detail-crumb" aria-label={t('kanban.title')}>
        <Link to="/kanban" className="detail-crumb__back">&larr; {t('kanban.board.back')}</Link>
        {ready && (
          <>
            <span className="detail-crumb__sep">/</span>
            <span className="detail-crumb__current">{kanban.name}</span>
          </>
        )}
      </nav>
      {ready ? (
        <div className="kanban-board__title">
          <EditableField
            value={kanban.name}
            variant={{ kind: 'text' }}
            required
            onCommit={commitName}
            label={t('kanban.board.nameLabel')}
          />
          {/* Niente filtro su una board che non si disegna: comporre un filtro per un risultato
              che non si vedra' mai e' un comando che promette qualcosa e non lo mantiene. Vale
              anche a catalogo VUOTO, che dal Task 16 e' una risposta legittima e non un guasto:
              il tipo non dichiara campi filtrabili, e non c'e' niente da comporre. */}
          <Button
            size="small"
            variant="outlined"
            disabled={!canRender || !filterFields.length}
            onClick={() => { setDraft(kanban.filter ?? null); setFilterOpen(true); }}
          >
            {conditionCount > 0
              ? t('kanban.filter.conditionCount', { count: conditionCount })
              : t('kanban.filter.edit')}
          </Button>
        </div>
      ) : (
        <h2 className="kanban-board__title" />
      )}

      {/* In pagina e non in una modale: resta sotto gli occhi mentre si guardano le
          colonne, che e' quando ci si fa la domanda a cui risponde. Il comando del filtro
          e' gia' nella riga del titolo, sopra. */}
      {orphans.length > 0 && (
        <div className="alert-warning">
          <div className="alert-warning__title">{t('kanban.filter.orphanTitle')}</div>
          {t('kanban.filter.orphanBody', {
            count: orphans.length,
            // Ambito e campo GREZZI, non tradotti: una condizione orfana e' per definizione fuori
            // catalogo, quindi non esiste un'etichetta da mostrare. Inventarne una nasconderebbe
            // proprio l'informazione utile — quale coppia il catalogo non riconosce piu'.
            fields: orphans.map((c) => `${c.scope} · ${c.fieldName}`).join(', '),
          })}
        </div>
      )}

      {ready && !canRender && (
        <div className="alert-warning">
          <div className="alert-warning__title">{t('kanban.board.unsupportedTypeTitle')}</div>
          {t('kanban.board.unsupportedTypeBody', { type: getEntityLabelByFqn(kanban.className, t) })}
        </div>
      )}

      {canRender && loading && <p className="text-muted list-feedback">{t('common.loading')}</p>}

      {canRender && !loading && states.length === 0 && (
        <p className="text-muted list-feedback">{t('kanban.board.noStates')}</p>
      )}

      {canRender && !loading && states.length > 0 && (
        <KanbanBoard<Record<string, unknown>>
          columns={columns}
          items={boardItems}
          onMove={handleMove}
          emptyLabel={t('kanban.board.emptyColumn')}
          renderCard={(item) => (board ? <board.Card item={item} /> : null)}
        />
      )}

      {/* In un dialog e non in pagina: la board e' il RISULTATO del filtro, e comporlo
          accanto al risultato che sta per cambiare confonde le due cose. */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('kanban.filter.editTitle')}</DialogTitle>
        <DialogContent>
          <KanbanFilterEditor
            filter={draft}
            fields={filterFields}
            onChange={setDraft}
            disabled={savingFilter}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterOpen(false)} disabled={savingFilter}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={saveFilter} disabled={savingFilter}>
            {savingFilter ? t('common.loading') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
